import React, { useState, useRef, useCallback, useEffect } from 'react';
import { AnnotationCanvas, AnnotationCanvasRef } from './components/Canvas/AnnotationCanvas';
import { FileSelector } from './components/FileSelector/FileSelector';
import { ControlPanel } from './components/ControlPanel/ControlPanel';
import { 
  AppState, 
  Point, 
  VertebraType, 
  EdgeType, 
  ImageFile,
  Annotations,
  AnnotationMode,
  ViewAngleType
} from './types';
import { 
  loadImageFiles, 
  parseOriginalCSV, 
  parseOriginalCSVForOBB,
  exportTriangleCSV,
  exportOBBCSV, 
  loadImage 
} from './utils/fileUtils';
import './App.css';

const INITIAL_STATE: AppState = {
  // Common properties
  currentImage: null,
  currentImageFile: null,
  originalLines: [],
  displayScale: 1,
  showOriginalLines: true,
  originalLinesOpacity: 0.7,
  availableImages: [],
  
  // Mode selection
  annotationMode: 'triangle',
  
  // Triangle mode properties
  annotations: {},
  currentTriangle: [],
  currentVertebra: 'L1',
  currentEdge: 'upper',
  triangleOpacity: 0.7,
  
  // OBB mode properties
  obbAnnotations: {},
  currentQuadrilateral: [],
  currentViewAngle: '正視',
  quadrilateralOpacity: 0.7,
  processedVertebrae: []
};

function App() {
  const [state, setState] = useState<AppState>(INITIAL_STATE);
  const canvasRef = useRef<AnnotationCanvasRef>(null);

  const updateState = useCallback((updates: Partial<AppState>) => {
    setState(prev => ({ ...prev, ...updates }));
  }, []);

  const handleDirectoryLoad = useCallback(async (files: FileList) => {
    try {
      const imageFiles = await loadImageFiles(files);
      updateState({ 
        availableImages: imageFiles,
        currentImageFile: null,
        currentImage: null,
        annotations: {},
        originalLines: [],
        currentTriangle: [],
        obbAnnotations: {},
        currentQuadrilateral: [],
        processedVertebrae: []
      });
    } catch (error) {
      console.error('載入資料夾時發生錯誤:', error);
      alert('載入資料夾時發生錯誤，請重試');
    }
  }, [updateState]);

  const moveToNextPosition = useCallback(() => {
    if (state.currentEdge === 'upper') {
      updateState({ currentEdge: 'lower', currentTriangle: [] });
    } else {
      const vertebrae: VertebraType[] = ['L1', 'L2', 'L3', 'L4', 'L5'];
      const currentIndex = vertebrae.indexOf(state.currentVertebra);
      if (currentIndex < vertebrae.length - 1) {
        const nextVertebra = vertebrae[currentIndex + 1];
        updateState({
          currentVertebra: nextVertebra,
          currentEdge: 'upper',
          currentTriangle: []
        });
      }
    }
  }, [state.currentEdge, state.currentVertebra, updateState]);

  const handleImageSelect = useCallback(async (imageFile: ImageFile) => {
    try {
      const image = await loadImage(imageFile.path);
      let originalLines: any[] = [];
      let obbQuadrilaterals: { [key: string]: Point[] } = {};

      if (imageFile.csvPath) {
        originalLines = await parseOriginalCSV(imageFile.csvPath);
        obbQuadrilaterals = await parseOriginalCSVForOBB(imageFile.csvPath);
      }

      // Convert quadrilaterals to initial OBB annotations for easy classification
      const initialObbAnnotations: typeof state.obbAnnotations = {};
      Object.entries(obbQuadrilaterals).forEach(([vertebraId, points]) => {
        initialObbAnnotations[vertebraId] = {
          viewAngle: '正視', // Default view angle, user will change this
          quadrilateral: {
            points: points as [Point, Point, Point, Point]
          }
        };
      });

      updateState({
        currentImageFile: imageFile,
        currentImage: image,
        originalLines,
        annotations: {},
        currentTriangle: [],
        currentVertebra: 'L1',
        currentEdge: 'upper',
        obbAnnotations: initialObbAnnotations,
        currentQuadrilateral: [],
        processedVertebrae: Object.keys(obbQuadrilaterals)
      });

      // Clear the temporary store since we now have proper annotations
      // @ts-ignore
      window.currentOBBQuadrilaterals = {};
    } catch (error) {
      console.error('載入影像時發生錯誤:', error);
      alert('載入影像時發生錯誤，請重試');
    }
  }, [updateState]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to check if point is inside quadrilateral
  const isPointInQuadrilateral = useCallback((point: Point, quad: Point[]): boolean => {
    if (quad.length !== 4) return false;
    
    // Use ray casting algorithm
    let inside = false;
    for (let i = 0, j = quad.length - 1; i < quad.length; j = i++) {
      if ((quad[i].y > point.y) !== (quad[j].y > point.y) &&
          point.x < (quad[j].x - quad[i].x) * (point.y - quad[i].y) / (quad[j].y - quad[i].y) + quad[i].x) {
        inside = !inside;
      }
    }
    return inside;
  }, []);

  const handleCanvasClick = useCallback((point: Point) => {
    if (state.annotationMode === 'triangle') {
      const newTriangle = [...state.currentTriangle, point];
      
      if (newTriangle.length === 3) {
        const newAnnotations = { ...state.annotations };
        
        if (!newAnnotations[state.currentVertebra]) {
          newAnnotations[state.currentVertebra] = {};
        }
        
        newAnnotations[state.currentVertebra]![state.currentEdge] = {
          points: newTriangle as [Point, Point, Point]
        };

        updateState({
          annotations: newAnnotations,
          currentTriangle: []
        });

        moveToNextPosition();
      } else {
        updateState({ currentTriangle: newTriangle });
      }
    } else if (state.annotationMode === 'obb') {
      // In OBB mode, clicking selects the vertebra for classification
      // Find which vertebra was clicked
      let clickedVertebra: string | null = null;
      
      for (const [vertebraId, annotation] of Object.entries(state.obbAnnotations)) {
        if (isPointInQuadrilateral(point, annotation.quadrilateral.points)) {
          clickedVertebra = vertebraId;
          break;
        }
      }
      
      if (clickedVertebra) {
        // Update the selected vertebra's view angle
        const newObbAnnotations = { ...state.obbAnnotations };
        newObbAnnotations[clickedVertebra] = {
          ...newObbAnnotations[clickedVertebra],
          viewAngle: state.currentViewAngle
        };
        
        updateState({ 
          obbAnnotations: newObbAnnotations
        });
      }
    }
  }, [state, updateState, moveToNextPosition, isPointInQuadrilateral]);


  const handleVertebraChange = useCallback((vertebra: VertebraType) => {
    updateState({ 
      currentVertebra: vertebra, 
      currentTriangle: [] 
    });
  }, [updateState]);

  const handleEdgeChange = useCallback((edge: EdgeType) => {
    updateState({ 
      currentEdge: edge, 
      currentTriangle: [] 
    });
  }, [updateState]);

  const handleUndo = useCallback(() => {
    if (state.annotationMode === 'triangle' && state.currentTriangle.length > 0) {
      const newTriangle = [...state.currentTriangle];
      newTriangle.pop();
      updateState({ currentTriangle: newTriangle });
    } else if (state.annotationMode === 'obb' && state.currentQuadrilateral.length > 0) {
      const newQuadrilateral = [...state.currentQuadrilateral];
      newQuadrilateral.pop();
      updateState({ currentQuadrilateral: newQuadrilateral });
    }
  }, [state.annotationMode, state.currentTriangle, state.currentQuadrilateral, updateState]);

  const handleClearCurrent = useCallback(() => {
    if (state.annotationMode === 'triangle') {
      updateState({ currentTriangle: [] });
    } else if (state.annotationMode === 'obb') {
      updateState({ currentQuadrilateral: [] });
    }
  }, [state.annotationMode, updateState]);

  const handleDeleteCompleted = useCallback(() => {
    const hasCompletedTriangle = state.annotations[state.currentVertebra]?.[state.currentEdge];
    if (hasCompletedTriangle) {
      const newAnnotations = { ...state.annotations };
      if (newAnnotations[state.currentVertebra]) {
        delete newAnnotations[state.currentVertebra]![state.currentEdge];
        
        // 如果該椎體沒有其他標記，刪除整個椎體記錄
        if (!newAnnotations[state.currentVertebra]!.upper && !newAnnotations[state.currentVertebra]!.lower) {
          delete newAnnotations[state.currentVertebra];
        }
      }
      updateState({ annotations: newAnnotations });
    }
  }, [state.annotations, state.currentVertebra, state.currentEdge, updateState]);

  const handleClearAll = useCallback(() => {
    if (window.confirm('確定要清除所有標記嗎？')) {
      updateState({ 
        annotations: {}, 
        currentTriangle: [],
        obbAnnotations: {},
        currentQuadrilateral: []
      });
    }
  }, [updateState]);

  const handleExport = useCallback(() => {
    try {
      if (state.annotationMode === 'triangle') {
        exportTriangleCSV(state.annotations, state.currentImageFile?.name);
        alert('三角形標記CSV檔案匯出成功！');
      } else if (state.annotationMode === 'obb') {
        exportOBBCSV(state.obbAnnotations, state.currentImageFile?.name);
        alert('OBB標記CSV檔案匯出成功！');
      }
    } catch (error) {
      console.error('匯出時發生錯誤:', error);
      alert('匯出時發生錯誤，請重試');
    }
  }, [state.annotationMode, state.annotations, state.obbAnnotations, state.currentImageFile?.name]);

  const handleImport = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      const lines = text.trim().split('\n');
      const headers = lines[0].split(',');

      if (headers[0] !== 'vertebra' || headers[1] !== 'edge') {
        alert('CSV格式不正確');
        return;
      }

      const newAnnotations: Annotations = {};

      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',');
        const vertebra = values[0] as VertebraType;
        const edge = values[1] as EdgeType;
        
        const points: [Point, Point, Point] = [
          { x: parseFloat(values[2]), y: parseFloat(values[3]) },
          { x: parseFloat(values[4]), y: parseFloat(values[5]) },
          { x: parseFloat(values[6]), y: parseFloat(values[7]) }
        ];

        if (!newAnnotations[vertebra]) {
          newAnnotations[vertebra] = {};
        }
        newAnnotations[vertebra]![edge] = { points };
      }

      updateState({ annotations: newAnnotations });
      alert('CSV檔案匯入成功！');
    } catch (error) {
      console.error('匯入時發生錯誤:', error);
      alert('匯入時發生錯誤，請重試');
    }
  }, [updateState]);

  const handleShowOriginalLinesToggle = useCallback(() => {
    updateState({ showOriginalLines: !state.showOriginalLines });
  }, [state.showOriginalLines, updateState]);

  const handleOpacityChange = useCallback((opacity: number) => {
    updateState({ originalLinesOpacity: opacity });
  }, [updateState]);

  const handleTriangleOpacityChange = useCallback((opacity: number) => {
    updateState({ triangleOpacity: opacity });
  }, [updateState]);

  // New handlers for OBB mode
  const handleModeChange = useCallback((mode: AnnotationMode) => {
    updateState({ 
      annotationMode: mode,
      currentTriangle: [],
      currentQuadrilateral: [],
      // 切換到OBB模式時自動關閉原始線段顯示
      showOriginalLines: mode === 'triangle' ? state.showOriginalLines : false
    });
  }, [updateState, state.showOriginalLines]);

  const handleViewAngleChange = useCallback((viewAngle: ViewAngleType) => {
    updateState({ currentViewAngle: viewAngle });
  }, [updateState]);

  const handleQuadrilateralOpacityChange = useCallback((opacity: number) => {
    updateState({ quadrilateralOpacity: opacity });
  }, [updateState]);

  const handleDeleteOBBAnnotation = useCallback((vertebraId: string) => {
    if (window.confirm(`確定要刪除 ${vertebraId} 的標記嗎？`)) {
      const newObbAnnotations = { ...state.obbAnnotations };
      delete newObbAnnotations[vertebraId];
      updateState({ obbAnnotations: newObbAnnotations });
    }
  }, [state.obbAnnotations, updateState]);

  // 圖片導航功能
  const handlePreviousImage = useCallback(() => {
    if (state.availableImages.length === 0 || !state.currentImageFile) return;
    
    const currentIndex = state.availableImages.findIndex(
      img => img.name === state.currentImageFile?.name
    );
    if (currentIndex > 0) {
      const previousImage = state.availableImages[currentIndex - 1];
      handleImageSelect(previousImage);
    }
  }, [state.availableImages, state.currentImageFile, handleImageSelect]);

  const handleNextImage = useCallback(() => {
    if (state.availableImages.length === 0 || !state.currentImageFile) return;
    
    const currentIndex = state.availableImages.findIndex(
      img => img.name === state.currentImageFile?.name
    );
    if (currentIndex < state.availableImages.length - 1) {
      const nextImage = state.availableImages[currentIndex + 1];
      handleImageSelect(nextImage);
    }
  }, [state.availableImages, state.currentImageFile, handleImageSelect]);

  // 透明度調整功能
  const handleIncreaseOpacity = useCallback(() => {
    const newOpacity = Math.min(1.0, state.originalLinesOpacity + 0.1);
    updateState({ originalLinesOpacity: newOpacity });
  }, [state.originalLinesOpacity, updateState]);

  const handleDecreaseOpacity = useCallback(() => {
    const newOpacity = Math.max(0.1, state.originalLinesOpacity - 0.1);
    updateState({ originalLinesOpacity: newOpacity });
  }, [state.originalLinesOpacity, updateState]);

  // 三角形透明度調整功能
  const handleIncreaseTriangleOpacity = useCallback(() => {
    const newOpacity = Math.min(1.0, state.triangleOpacity + 0.1);
    updateState({ triangleOpacity: newOpacity });
  }, [state.triangleOpacity, updateState]);

  const handleDecreaseTriangleOpacity = useCallback(() => {
    const newOpacity = Math.max(0.1, state.triangleOpacity - 0.1);
    updateState({ triangleOpacity: newOpacity });
  }, [state.triangleOpacity, updateState]);


  // 全局鍵盤快捷鍵監聽
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 如果正在輸入框中輸入，忽略快捷鍵
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
        return;
      }

      // 防止在某些快捷鍵操作時觸發默認行為
      const shouldPreventDefault = ['ArrowLeft', 'ArrowRight', 'KeyA', 'KeyD', 'Equal', 'Minus'].includes(e.code);
      if (shouldPreventDefault) {
        e.preventDefault();
      }

      switch (e.code) {
        // 圖片導航
        case 'ArrowLeft':
        case 'KeyA':
          handlePreviousImage();
          break;
        case 'ArrowRight':
        case 'KeyD':
          handleNextImage();
          break;

        // 標記操作
        case 'KeyZ':
          if (!e.ctrlKey) handleUndo();
          break;
        case 'KeyC':
          if (e.ctrlKey && e.shiftKey) {
            handleClearAll();
          } else if (!e.ctrlKey) {
            handleClearCurrent();
          }
          break;
        case 'KeyX':
          if (!e.ctrlKey) {
            handleDeleteCompleted();
          }
          break;
        case 'KeyE':
          if (e.ctrlKey) {
            e.preventDefault();
            handleExport();
          } else if (state.annotationMode === 'obb') {
            handleViewAngleChange('俯視');
          }
          break;

        // 椎體切換 (1-5) - 只在三角形模式有效
        case 'Digit1':
          if (state.annotationMode === 'triangle') {
            handleVertebraChange('L1');
          }
          break;
        case 'Digit2':
          if (state.annotationMode === 'triangle') {
            handleVertebraChange('L2');
          }
          break;
        case 'Digit3':
          if (state.annotationMode === 'triangle') {
            handleVertebraChange('L3');
          }
          break;
        case 'Digit4':
          if (state.annotationMode === 'triangle') {
            handleVertebraChange('L4');
          }
          break;
        case 'Digit5':
          if (state.annotationMode === 'triangle') {
            handleVertebraChange('L5');
          }
          break;

        // 邊緣切換 - 只在三角形模式有效
        case 'KeyU':
          if (state.annotationMode === 'triangle') {
            handleEdgeChange('upper');
          }
          break;
        case 'KeyL':
          if (state.annotationMode === 'triangle') {
            handleEdgeChange('lower');
          }
          break;

        // 模式切換
        case 'KeyM':
          if (e.shiftKey) {
            handleModeChange(state.annotationMode === 'triangle' ? 'obb' : 'triangle');
          }
          break;

        // OBB模式視角切換
        case 'KeyQ':
          if (state.annotationMode === 'obb') {
            handleViewAngleChange('仰視');
          }
          break;
        case 'KeyW':
          if (state.annotationMode === 'obb') {
            handleViewAngleChange('正視');
          }
          break;

        // 顯示控制
        case 'KeyV':
          handleShowOriginalLinesToggle();
          break;
        case 'Equal': // + 鍵
          if (e.shiftKey && state.annotationMode === 'triangle') {
            handleIncreaseTriangleOpacity();
          } else if (e.shiftKey && state.annotationMode === 'obb') {
            const newOpacity = Math.min(1.0, state.quadrilateralOpacity + 0.1);
          updateState({ quadrilateralOpacity: newOpacity });
          } else {
            handleIncreaseOpacity();
          }
          break;
        case 'Minus': // - 鍵
          if (e.shiftKey && state.annotationMode === 'triangle') {
            handleDecreaseTriangleOpacity();
          } else if (e.shiftKey && state.annotationMode === 'obb') {
            const newOpacity = Math.max(0.1, state.quadrilateralOpacity - 0.1);
          updateState({ quadrilateralOpacity: newOpacity });
          } else {
            handleDecreaseOpacity();
          }
          break;

        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    handlePreviousImage,
    handleNextImage,
    handleUndo,
    handleClearCurrent,
    handleDeleteCompleted,
    handleClearAll,
    handleExport,
    handleVertebraChange,
    handleEdgeChange,
    handleShowOriginalLinesToggle,
    handleIncreaseOpacity,
    handleDecreaseOpacity,
    handleIncreaseTriangleOpacity,
    handleDecreaseTriangleOpacity,
    handleModeChange,
    handleViewAngleChange,
    state.annotationMode,
    state.quadrilateralOpacity,
    updateState
  ]);

  return (
    <div className="app">
      <div className="container">
        <div className="header">
          <h1>{state.annotationMode === 'triangle' ? '🔺 BUU 椎體三角形標記工具' : '🔶 BUU 椎體視角分類標記工具'} (React版)</h1>
          <p>
            {state.annotationMode === 'triangle' 
              ? '針對非正視角X光影像的椎體邊緣進行三角形標記。每個椎體的上緣和下緣分別標記為三角形，以捕捉投影重疊的邊緣結構。'
              : '對X光影像中的椎體進行視角分類標記。系統自動將原始標記合併為四邊形，您只需點擊椎體區域並分類為仰視（橙色）、正視（綠色）或俯視（藍色）角度，用於YOLO-OBB模型訓練。'
            }
          </p>
        </div>

        <div className="instructions">
          <h3>📋 使用說明</h3>
          <ol>
            <li><strong>選擇資料夾</strong>：點擊上方區域選擇包含影像和CSV檔案的資料夾</li>
            <li><strong>選擇影像</strong>：從影像列表中選擇要標記的影像，系統會自動載入對應的CSV檔案</li>
            {state.annotationMode === 'triangle' ? (
              <>
                <li><strong>選擇椎體和邊緣</strong>：選擇要標記的椎體(L1-L5)和邊緣(上緣/下緣)</li>
                <li><strong>標記三角形</strong>：在影像上點擊三個點形成三角形</li>
              </>
            ) : (
              <>
                <li><strong>選擇視角</strong>：點擊仰視（橙色）、正視（綠色）或俯視（藍色）按鈕</li>
                <li><strong>分類椎體</strong>：點擊椎體四邊形區域進行視角分類</li>
              </>
            )}
            <li><strong>調整顯示</strong>：可以顯示/隱藏原始線段或調整透明度</li>
            <li><strong>匯出結果</strong>：完成標記後匯出CSV格式的標註資料</li>
          </ol>
          
          <div className="keyboard-shortcuts">
            <h4>⌨️ 鍵盤快捷鍵</h4>
            <div className="shortcuts-grid">
              <div className="shortcut-category">
                <h5>🖼️ 圖片導航</h5>
                <div className="shortcut-item">
                  <kbd>←</kbd> <kbd>A</kbd> <span>上一張圖片</span>
                </div>
                <div className="shortcut-item">
                  <kbd>→</kbd> <kbd>D</kbd> <span>下一張圖片</span>
                </div>
              </div>
              
              <div className="shortcut-category">
                <h5>✏️ 標記操作</h5>
                {state.annotationMode === 'triangle' ? (
                  <>
                    <div className="shortcut-item">
                      <kbd>Z</kbd> <span>撤銷上一點</span>
                    </div>
                    <div className="shortcut-item">
                      <kbd>C</kbd> <span>清除當前三角形</span>
                    </div>
                    <div className="shortcut-item">
                      <kbd>X</kbd> <span>刪除已完成標記</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="shortcut-item">
                      <kbd>Q</kbd> <span>選擇仰視角度</span>
                    </div>
                    <div className="shortcut-item">
                      <kbd>W</kbd> <span>選擇正視角度</span>
                    </div>
                    <div className="shortcut-item">
                      <kbd>E</kbd> <span>選擇俯視角度</span>
                    </div>
                  </>
                )}
                <div className="shortcut-item">
                  <kbd>Ctrl</kbd>+<kbd>E</kbd> <span>匯出CSV</span>
                </div>
                <div className="shortcut-item">
                  <kbd>Ctrl</kbd>+<kbd>Shift</kbd>+<kbd>C</kbd> <span>清除所有標記</span>
                </div>
              </div>
              
              {state.annotationMode === 'triangle' && (
                <div className="shortcut-category">
                  <h5>🔢 椎體選擇</h5>
                  <div className="shortcut-item">
                    <kbd>1</kbd>-<kbd>5</kbd> <span>選擇 L1-L5 椎體</span>
                  </div>
                  <div className="shortcut-item">
                    <kbd>U</kbd> <span>切換到上緣</span>
                  </div>
                  <div className="shortcut-item">
                    <kbd>L</kbd> <span>切換到下緣</span>
                  </div>
                </div>
              )}
              
              <div className="shortcut-category">
                <h5>👁️ 顯示控制</h5>
                <div className="shortcut-item">
                  <kbd>V</kbd> <span>切換顯示原始線段</span>
                </div>
                <div className="shortcut-item">
                  <kbd>+</kbd> <span>增加原始線段透明度</span>
                </div>
                <div className="shortcut-item">
                  <kbd>-</kbd> <span>減少原始線段透明度</span>
                </div>
                <div className="shortcut-item">
                  <kbd>Shift</kbd>+<kbd>+</kbd> <span>增加{state.annotationMode === 'triangle' ? '三角形' : '四邊形'}透明度</span>
                </div>
                <div className="shortcut-item">
                  <kbd>Shift</kbd>+<kbd>-</kbd> <span>減少{state.annotationMode === 'triangle' ? '三角形' : '四邊形'}透明度</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="main-layout">
          <div className="file-section">
            <FileSelector
              availableImages={state.availableImages}
              currentImageFile={state.currentImageFile}
              onDirectoryLoad={handleDirectoryLoad}
              onImageSelect={handleImageSelect}
            />
          </div>

          <div className="image-section">
            <div className="canvas-wrapper">
              <AnnotationCanvas
                ref={canvasRef}
                image={state.currentImage}
                annotations={state.annotations}
                originalLines={state.originalLines}
                currentTriangle={state.currentTriangle}
                currentVertebra={state.currentVertebra}
                currentEdge={state.currentEdge}
                showOriginalLines={state.showOriginalLines}
                originalLinesOpacity={state.originalLinesOpacity}
                triangleOpacity={state.triangleOpacity}
                onCanvasClick={handleCanvasClick}
                // New OBB mode props
                annotationMode={state.annotationMode}
                obbAnnotations={state.obbAnnotations}
                currentQuadrilateral={state.currentQuadrilateral}
                currentViewAngle={state.currentViewAngle}
                quadrilateralOpacity={state.quadrilateralOpacity}
                processedVertebrae={state.processedVertebrae}
              />
            </div>
          </div>

          <div className="control-section">
            <ControlPanel
              currentVertebra={state.currentVertebra}
              currentEdge={state.currentEdge}
              currentTriangleLength={state.currentTriangle.length}
              annotations={state.annotations}
              showOriginalLines={state.showOriginalLines}
              originalLinesOpacity={state.originalLinesOpacity}
              triangleOpacity={state.triangleOpacity}
              currentImageFile={state.currentImageFile}
              hasOriginalLines={state.originalLines.length > 0}
              onVertebraChange={handleVertebraChange}
              onEdgeChange={handleEdgeChange}
              onUndo={handleUndo}
              onClearCurrent={handleClearCurrent}
              onDeleteCompleted={handleDeleteCompleted}
              onClearAll={handleClearAll}
              onExport={handleExport}
              onImport={handleImport}
              onShowOriginalLinesToggle={handleShowOriginalLinesToggle}
              onOpacityChange={handleOpacityChange}
              onTriangleOpacityChange={handleTriangleOpacityChange}
              // New OBB mode props
              annotationMode={state.annotationMode}
              currentViewAngle={state.currentViewAngle}
              currentQuadrilateralLength={state.currentQuadrilateral.length}
              obbAnnotations={state.obbAnnotations}
              quadrilateralOpacity={state.quadrilateralOpacity}
              processedVertebrae={state.processedVertebrae}
              onModeChange={handleModeChange}
              onViewAngleChange={handleViewAngleChange}
              onQuadrilateralOpacityChange={handleQuadrilateralOpacityChange}
              onDeleteOBBAnnotation={handleDeleteOBBAnnotation}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
